import { z } from 'zod';

import { calendarDateSchema, idSchema } from '../common';
import { LIMITS } from '../limits';

/**
 * The filter language of the board toolbar (TLB-1..5), later the dashboard (DSH-2) and saved views
 * (v1.1). One model, one evaluator, run by the web app and the API (ADR-0009).
 */

const ids = z.array(idSchema).max(200);
const dateRange = z
  .object({ from: calendarDateSchema, to: calendarDateSchema })
  .refine((range) => range.to >= range.from, {
    message: 'The range must end on or after its start.',
  });

/** Special owner values next to user IDs (TLB-2, TLB-3). */
export const ME = 'me';
export const UNASSIGNED = 'unassigned';
const ownerValue = z.union([idSchema, z.literal(ME), z.literal(UNASSIGNED)]);

export const DUE_PRESETS = [
  'today',
  'tomorrow',
  'thisWeek',
  'nextWeek',
  'overdue',
  'none',
] as const;
export const TIMELINE_PRESETS = ['thisWeek', 'thisMonth'] as const;

const field = <T extends string>(name: T) => z.literal(name);

/** One condition. Each field accepts only its own operators, and each operator its own value. */
export const filterConditionSchema = z.discriminatedUnion('field', [
  z.object({ field: field('status'), op: z.enum(['anyOf', 'noneOf']), value: ids }),
  z.object({
    field: field('owner'),
    op: z.enum(['anyOf', 'noneOf']),
    value: z.array(ownerValue).max(200),
  }),
  z.discriminatedUnion('op', [
    z.object({ field: field('dueDate'), op: z.literal('is'), value: z.enum(DUE_PRESETS) }),
    z.object({ field: field('dueDate'), op: z.literal('between'), value: dateRange }),
    z.object({
      field: field('dueDate'),
      op: z.enum(['before', 'after']),
      value: calendarDateSchema,
    }),
  ]),
  z.discriminatedUnion('op', [
    z.object({ field: field('priority'), op: z.enum(['anyOf', 'noneOf']), value: ids }),
    z.object({ field: field('priority'), op: z.literal('isEmpty') }),
  ]),
  z.discriminatedUnion('op', [
    z.object({
      field: field('timeline'),
      op: z.literal('overlaps'),
      value: z.union([z.enum(TIMELINE_PRESETS), dateRange]),
    }),
    z.object({ field: field('timeline'), op: z.literal('isEmpty') }),
  ]),
  z.object({ field: field('group'), op: z.literal('anyOf'), value: ids }),
  z.object({
    field: field('title'),
    op: z.enum(['contains', 'notContains']),
    value: z.string().max(LIMITS.searchQueryMax),
  }),
  z.discriminatedUnion('op', [
    z.object({
      field: field('notes'),
      op: z.literal('contains'),
      value: z.string().max(LIMITS.searchQueryMax),
    }),
    z.object({ field: field('notes'), op: z.enum(['isEmpty', 'isNotEmpty']) }),
  ]),
  z.object({
    field: field('updated'),
    op: z.literal('withinDays'),
    value: z.number().int().min(1).max(365),
  }),
  z.object({ field: field('createdBy'), op: z.literal('anyOf'), value: ids }),
]);

export const filterSchema = z.object({
  join: z.enum(['and', 'or']),
  conditions: z.array(filterConditionSchema).max(20),
});

export const SORT_FIELDS = [
  'title',
  'status',
  'owner',
  'dueDate',
  'priority',
  'timeline',
  'updated',
  'created',
  'key',
] as const;

export const sortSchema = z
  .array(z.object({ field: z.enum(SORT_FIELDS), dir: z.enum(['asc', 'desc']) }))
  .max(3);

export const GROUP_BY = ['group', 'status', 'priority', 'owner', 'dueBucket'] as const;
export const groupBySchema = z.enum(GROUP_BY);

export type FilterCondition = z.infer<typeof filterConditionSchema>;
export type FilterField = FilterCondition['field'];
export type Filter = z.infer<typeof filterSchema>;
export type SortSpec = z.infer<typeof sortSchema>;
export type SortField = (typeof SORT_FIELDS)[number];
export type GroupBy = z.infer<typeof groupBySchema>;
export type DuePreset = (typeof DUE_PRESETS)[number];
export type TimelinePreset = (typeof TIMELINE_PRESETS)[number];

/** The fields of a work item the engine reads. A board `Task` satisfies it. */
export interface FilterableItem {
  readonly id: string;
  readonly key: string;
  readonly title: string;
  readonly notes: string;
  readonly statusId: string;
  readonly priorityId: string | null;
  readonly ownerIds: readonly string[];
  readonly dueDate: string | null;
  readonly timeline: { readonly start: string; readonly end: string } | null;
  readonly groupId: string;
  readonly parentId: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Everything relative is resolved from here, never from the system clock (ADR-0009). */
export interface FilterContext {
  /** The viewer's calendar date (their timezone). */
  readonly today: string;
  /** The current instant (ISO), for "updated within N days". */
  readonly now: string;
  readonly meId: string;
  /** Status and priority labels by ID: category decides "done", position decides sort order. */
  readonly labels: ReadonlyMap<
    string,
    { readonly category: string | null; readonly position: number }
  >;
  /** Display names for sorting by owner. */
  readonly personName?: (id: string) => string;
}
